import React, {useCallback, useRef} from 'react';
import {DraggableCore, type DraggableData, type DraggableEvent} from 'react-draggable';
import {PopupDialog} from 'app/utils/modals/PopupDialog';
import {modalsStore} from 'app/store/global';
import {useClickOutside} from 'app/utils/useClickOutside';

const CLOSE_THRESHOLD_RATIO = 0.9;

interface CasinoMobileSheetProps {
    popupId: string
    title: string
    children?: React.ReactNode
}

export default function CasinoMobileSheet({popupId, title, children}: CasinoMobileSheetProps): React.ReactNode {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const startHeightRef = useRef<number>(0);
    const dragStartYRef = useRef<number>(0);

    const handleClose = useCallback((): void => {
        modalsStore.hideModal(popupId);
    }, [popupId]);

    useClickOutside(contentRef, handleClose);

    const setSheetHeight = useCallback((height: number): void => {
        const el = contentRef.current;

        if (!el) {
            return;
        }

        el.style.height = `${height}px`;
    }, []);

    const handleDragStart = useCallback((_e: DraggableEvent, data: DraggableData): void => {
        const el = contentRef.current;

        if (!el) {
            return;
        }

        startHeightRef.current = el.getBoundingClientRect().height;
        dragStartYRef.current = data.y;
    }, []);

    const handleDrag = useCallback((_e: DraggableEvent, data: DraggableData): void => {
        const el = contentRef.current;

        if (!el) {
            return;
        }

        const nextHeight = startHeightRef.current + (dragStartYRef.current - data.y);
        const clampedHeight = Math.max(0, nextHeight);

        setSheetHeight(clampedHeight);
    }, [setSheetHeight]);

    const handleDragStop = useCallback((): void => {
        const el = contentRef.current;

        if (!el) {
            return;
        }

        const startHeight = startHeightRef.current;
        const currentHeight = el.getBoundingClientRect().height;

        if (currentHeight < startHeight * CLOSE_THRESHOLD_RATIO) {
            handleClose();
            return;
        }

        setSheetHeight(startHeight);
    }, [handleClose, setSheetHeight]);

    return (
        <PopupDialog id={popupId} hideCloseButton>
            <DraggableCore
                nodeRef={contentRef}
                handle=".casino-mobile-sheet__handle"
                onStart={handleDragStart}
                onDrag={handleDrag}
                onStop={handleDragStop}
            >
                <div ref={contentRef} className="popup__content casino-mobile-sheet">
                    <div
                        className="casino-mobile-sheet__handle"
                        role="presentation"
                    />
                    <h2 className="casino-mobile-sheet__title">{title}</h2>
                    <div className="casino-mobile-sheet__divider" />
                    <div className="casino-mobile-sheet__body">
                        {children}
                    </div>
                    <div className="casino-mobile-sheet__footer" />
                </div>
            </DraggableCore>
        </PopupDialog>
    );
}
